import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Clock,
  DollarSign,
  CheckCircle,
  Brain,
  Calculator,
  MapPin,
  Lightbulb,
  Target,
  RefreshCw
} from "lucide-react";
import { Header, Footer } from "../../../../components/Layout";

const CognitivoPage = () => {
  const principles = [
    {
      title: "Los pensamientos influyen en las emociones",
      description: "Cómo interpretamos los eventos determina cómo nos sentimos y actuamos. Cambiar el pensamiento cambia el comportamiento.",
      icon: Brain
    },
    {
      title: "Distorsiones cognitivas",
      description: "Las adicciones se mantienen por errores de pensamiento como 'solo una vez más', 'merezco relajarme', 'puedo controlarlo'.",
      icon: Target
    },
    {
      title: "Creencias centrales",
      description: "Trabajamos con las creencias profundas que subyacen a la adicción: baja autoestima, necesidad de escape, etc.",
      icon: Lightbulb
    },
    {
      title: "Reestructuración",
      description: "Aprender a identificar pensamientos automáticos y reemplazarlos por pensamientos más realistas y saludables.",
      icon: RefreshCw
    }
  ];

  const techniques = [
    "Registro de pensamientos automáticos",
    "Identificación de distorsiones cognitivas",
    "Debate socrático de creencias irracionales",
    "Experimentos conductuales",
    "Técnicas de desactivación",
    "Trabajo con creencias nucleares"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Terapia Cognitiva para Adicciones",
    "description": "Tratamiento cognitivo que se enfoca en identificar y modificar los pensamientos distorsionados que mantienen la adicción."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Residencial", "item": "https://sinadicciones.org/tratamiento/residencial" },
      { "@type": "ListItem", "position": 4, "name": "Cognitivo", "item": "https://sinadicciones.org/tratamiento/residencial/cognitivo" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Terapia Cognitiva para Adicciones: Enfoque Residencial | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento cognitivo residencial para adicciones. Modifica pensamientos distorsionados que mantienen el consumo. Técnicas de reestructuración cognitiva." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/residencial/cognitivo" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/residencial/cognitivo" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">Tratamientos</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamiento/residencial" className="hover:text-emerald-400 transition-colors">Residencial</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Cognitivo</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                <Brain className="w-7 h-7 text-purple-400" />
              </div>
              <div className="px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
                <span className="text-purple-400 font-medium">Enfoque en pensamientos</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Terapia{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">
                Cognitiva
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Cambia tu forma de pensar para cambiar tu forma de actuar. 
              La terapia cognitiva te ayuda a identificar y modificar los pensamientos 
              que alimentan la adicción.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Brain className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">Pensamientos</p>
                <p className="text-slate-400 text-sm">foco principal</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">3-6 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Target className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">Estructurado</p>
                <p className="text-slate-400 text-sm">enfoque</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?approach=cognitivo"
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Centros con Enfoque Cognitivo
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Principios de la Terapia Cognitiva
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Entender cómo funcionan los pensamientos es el primer paso para cambiarlos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-purple-500/20"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <principle.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{principle.title}</h3>
                <p className="text-slate-400">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Techniques */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Técnicas que Aprenderás
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {techniques.map((technique, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">{technique}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900/50 to-slate-800/50 border border-purple-500/20 p-8 md:p-12 text-center">
            <Brain className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra Centros con Enfoque Cognitivo
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Busca centros residenciales que utilicen terapia cognitiva como parte de su programa.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros
              </Link>
              <Link 
                to="/tratamiento/residencial"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5" />
                Otros Enfoques
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Enfoques Terapéuticos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Conductual", path: "/tratamiento/residencial/conductual", color: "text-orange-400" },
              { title: "Cognitivo-Conductual", path: "/tratamiento/residencial/cognitivo-conductual", color: "text-emerald-400" },
              { title: "Holístico", path: "/tratamiento/residencial/holistico", color: "text-green-400" },
              { title: "Integrativo", path: "/tratamiento/residencial/integrativo", color: "text-pink-400" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-purple-500/30 transition-all group"
              >
                <span className={`font-medium ${item.color}`}>{item.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CognitivoPage;
